import { Controller } from 'stimulus';

// Jobs index, table view: per-user column visibility and widths.
//
// Lives on the page wrapper (jobs/index.html.erb) because the Columns pill
// sits in the filter row while the table renders below. Both hidden columns
// and widths are applied as CSS rules written into the table's inline <style>
// (rendered server-side for first paint, rewritten here on every change), so
// rows appended later by infinite scroll match with no per-row work.
//
// The frozen ID / Watch / Name columns are native position: sticky. Their
// `left` offsets depend on the widths of the frozen columns before them, so
// they are measured from the header cells and written into the same <style>
// after every change (hide, show, resize).
//
// Column order: the server renders columns in the user's saved order (see
// Jobs::TableColumns), so the DOM order IS the current order. Dragging a
// non-frozen header (pointerdown on its .jt-h, then a few px of movement)
// moves that column's header cell and every row cell to the drop position,
// mirrors the Columns menu, and saves the new order. The frozen ID / Watch /
// Name block is not draggable and always leads.
//
// Persistence: PATCH /jobs/table_prefs with the complete state (the server
// replaces, not merges). One request per toggle, finished resize, or drop.
const MIN_WIDTH = 48;
const MAX_WIDTH = 1200;
const DRAG_THRESHOLD = 6; // px of pointer travel before a header drag begins
const EDGE_SCROLL_ZONE = 48; // px from the table edge that auto-scrolls during a drag
const EDGE_SCROLL_STEP = 14;

export default class extends Controller {
  static targets = ['wrap', 'table', 'style', 'menu', 'toggleButton', 'checkbox', 'badge'];

  connect() {
    this.hidden = new Set(this.parseJson(this.data.get('hidden'), []));
    this.widths = this.parseJson(this.data.get('widths'), {});
    this.order = this.parseJson(this.data.get('order'), []);
    this.defaultOrder = this.parseJson(this.data.get('defaultOrder'), []);
    this.url = this.data.get('url');

    this._onDocClick = (event) => {
      if (!this.hasMenuTarget) return;
      const inMenu = this.menuTarget.contains(event.target);
      const onButton = this.hasToggleButtonTarget && this.toggleButtonTarget.contains(event.target);
      if (!inMenu && !onButton) this.closeMenu();
    };
    document.addEventListener('click', this._onDocClick);

    this._onWindowResize = () => this.scheduleRender();
    window.addEventListener('resize', this._onWindowResize);

    // A header label is a sort link; the browser would otherwise start a
    // native link drag as soon as the pointer moves on it.
    this._onDragStart = (event) => event.preventDefault();
    if (this.hasTableTarget) this.tableTarget.addEventListener('dragstart', this._onDragStart);

    this.render();
    this.watchFrozenWidths();
    this.watchAppendedRows();
  }

  disconnect() {
    document.removeEventListener('click', this._onDocClick);
    window.removeEventListener('resize', this._onWindowResize);
    if (this.hasTableTarget) this.tableTarget.removeEventListener('dragstart', this._onDragStart);
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._rowObserver) this._rowObserver.disconnect();
    if (this._raf) cancelAnimationFrame(this._raf);
    this.endDrag();
  }

  // The sticky offsets are measured from the frozen header cells, whose
  // widths can still change after connect: the star header is an icon-font
  // glyph (wider until the font loads), and a longer custom ID appended by
  // infinite scroll can widen the ID column. Re-measure whenever they change.
  watchFrozenWidths() {
    if (!this.hasTableTarget) return;
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this.scheduleRender());
      this.tableTarget.querySelectorAll('thead th.jt-sticky').forEach((th) => this._resizeObserver.observe(th));
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.scheduleRender()).catch(() => {});
    }
  }

  // ── Columns menu ───────────────────────────────────────────────────────────

  toggleMenu(event) {
    event.stopPropagation();
    this.menuTarget.classList.toggle('is-open');
  }

  closeMenu() {
    if (this.hasMenuTarget) this.menuTarget.classList.remove('is-open');
  }

  toggleColumn(event) {
    const box = event.currentTarget;
    const key = box.value;
    if (!key) return;
    if (box.checked) this.hidden.delete(key);
    else this.hidden.add(key);
    this.render();
    this.persist();
  }

  reset() {
    this.hidden.clear();
    this.widths = {};
    this.checkboxTargets.forEach((box) => { box.checked = true; });
    this.applyOrder(this.defaultOrder);
    this.order = [];
    this.render();
    this.persist({ reset: true });
    this.closeMenu();
  }

  // ── Resize ─────────────────────────────────────────────────────────────────
  // Pointer events cover mouse, touch, and pen; capture keeps the drag alive
  // when the pointer leaves the 8px handle.

  startResize(event) {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const handle = event.currentTarget;
    const th = handle.closest('th');
    const head = th && th.querySelector('.jt-h');
    const key = th && th.dataset.col;
    if (!head || !key) return;

    const startX = event.clientX;
    const startWidth = head.getBoundingClientRect().width;
    let moved = false;

    this.wrapTarget.classList.add('is-resizing');
    th.classList.add('is-resizing');

    const move = (ev) => {
      const width = Math.round(startWidth + (ev.clientX - startX));
      this.widths[key] = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));
      moved = true;
      this.scheduleRender();
    };
    const finish = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', finish);
      handle.removeEventListener('pointercancel', finish);
      if (handle.hasPointerCapture && handle.hasPointerCapture(event.pointerId)) {
        handle.releasePointerCapture(event.pointerId);
      }
      this.wrapTarget.classList.remove('is-resizing');
      th.classList.remove('is-resizing');
      this.render();
      if (moved) this.persist();
    };

    if (handle.setPointerCapture) handle.setPointerCapture(event.pointerId);
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', finish);
    handle.addEventListener('pointercancel', finish);
  }

  // Double-click on a handle: back to the automatic width.
  resetWidth(event) {
    event.preventDefault();
    event.stopPropagation();
    const th = event.currentTarget.closest('th');
    const key = th && th.dataset.col;
    if (!key || !(key in this.widths)) return;
    delete this.widths[key];
    this.render();
    this.persist();
  }

  // The handle sits inside a header cell whose label is a sort link; keep a
  // click that ends a drag from bubbling into anything above it.
  swallow(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Frozen-edge shadow: only once the table has scrolled horizontally.
  onScroll(event) {
    const scrolled = event.currentTarget.scrollLeft > 0;
    event.currentTarget.classList.toggle('is-scrolled-x', scrolled);
  }

  // ── Reorder (drag a header) ────────────────────────────────────────────────

  startDrag(event) {
    if (event.button !== undefined && event.button !== 0) return;
    if (event.target.closest('.jt-rz')) return;
    const th = event.currentTarget.closest('th');
    if (!th || th.classList.contains('jt-sticky') || !th.dataset.col) return;
    // Cancelling pointerdown stops text selection and the native link drag
    // but still lets a plain click reach the sort link.
    event.preventDefault();

    this._drag = {
      th,
      key: th.dataset.col,
      startX: event.clientX,
      pointerX: event.clientX,
      pointerY: event.clientY,
      active: false,
      target: null, // { th, before }: where the column would land
      ghost: null,
      line: null
    };
    this._onDragMove = (ev) => this.dragMove(ev);
    this._onDragUp = () => this.dragEnd();
    document.addEventListener('pointermove', this._onDragMove);
    document.addEventListener('pointerup', this._onDragUp);
    document.addEventListener('pointercancel', this._onDragUp);
  }

  dragMove(event) {
    const drag = this._drag;
    if (!drag) return;
    drag.pointerX = event.clientX;
    drag.pointerY = event.clientY;
    if (!drag.active) {
      if (Math.abs(event.clientX - drag.startX) < DRAG_THRESHOLD) return;
      this.beginDrag();
    }
    this.updateDrag();
  }

  beginDrag() {
    const drag = this._drag;
    drag.active = true;
    drag.th.classList.add('is-dragging-col');
    this.wrapTarget.classList.add('is-dragging');

    drag.ghost = document.createElement('div');
    drag.ghost.className = 'jt-drag-ghost';
    const label = drag.th.querySelector('.jt-label');
    drag.ghost.textContent = label ? label.textContent : drag.key;
    document.body.appendChild(drag.ghost);

    drag.line = document.createElement('div');
    drag.line.className = 'jt-drop-line';
    this.wrapTarget.appendChild(drag.line);

    this.edgeScrollLoop();
  }

  // Drop target: the first visible movable header whose midpoint is right of
  // the pointer; none means "after the last column".
  updateDrag() {
    const drag = this._drag;
    if (!drag || !drag.active) return;
    drag.ghost.style.left = `${drag.pointerX}px`;
    drag.ghost.style.top = `${drag.pointerY}px`;

    let target = null;
    this.movableHeaders().some((th) => {
      if (th === drag.th) return false;
      const rect = th.getBoundingClientRect();
      if (rect.width === 0) return false;
      if (drag.pointerX < rect.left + rect.width / 2) {
        target = { th, before: true };
        return true; // first header to the right of the pointer: stop
      }
      target = { th, before: false };
      return false;
    });
    drag.target = target;

    const wrapRect = this.wrapTarget.getBoundingClientRect();
    let x;
    if (target) {
      const rect = target.th.getBoundingClientRect();
      x = (target.before ? rect.left : rect.right) - wrapRect.left + this.wrapTarget.scrollLeft;
    } else {
      x = this.wrapTarget.scrollLeft; // no other movable column: nothing to show
    }
    drag.line.style.left = `${Math.round(x) - 1}px`;
    drag.line.style.top = `${this.wrapTarget.scrollTop}px`;
    drag.line.style.height = `${this.wrapTarget.clientHeight}px`;
    drag.line.style.display = target ? 'block' : 'none';
  }

  // Auto-scroll the table sideways while the pointer sits near either edge.
  edgeScrollLoop() {
    const drag = this._drag;
    if (!drag || !drag.active) return;
    const rect = this.wrapTarget.getBoundingClientRect();
    if (drag.pointerX > rect.right - EDGE_SCROLL_ZONE) {
      this.wrapTarget.scrollLeft += EDGE_SCROLL_STEP;
      this.updateDrag();
    } else if (drag.pointerX < rect.left + EDGE_SCROLL_ZONE) {
      this.wrapTarget.scrollLeft -= EDGE_SCROLL_STEP;
      this.updateDrag();
    }
    drag.scrollRaf = requestAnimationFrame(() => this.edgeScrollLoop());
  }

  dragEnd() {
    const drag = this._drag;
    if (!drag) return;
    const dropped = drag.active && drag.target;
    if (dropped) this.moveColumn(drag.key, drag.target.th.dataset.col, drag.target.before);
    const wasActive = drag.active;
    this.endDrag();
    if (wasActive) {
      // Swallow the click the browser fires after pointerup so the sort link
      // under the pointer doesn't navigate.
      const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
      document.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 0);
    }
    if (dropped) {
      this.order = this.currentOrder();
      this.render();
      this.persist();
    }
  }

  endDrag() {
    const drag = this._drag;
    if (!drag) return;
    document.removeEventListener('pointermove', this._onDragMove);
    document.removeEventListener('pointerup', this._onDragUp);
    document.removeEventListener('pointercancel', this._onDragUp);
    if (drag.scrollRaf) cancelAnimationFrame(drag.scrollRaf);
    drag.th.classList.remove('is-dragging-col');
    if (this.hasWrapTarget) this.wrapTarget.classList.remove('is-dragging');
    if (drag.ghost) drag.ghost.remove();
    if (drag.line) drag.line.remove();
    this._drag = null;
  }

  movableHeaders() {
    return Array.from(this.tableTarget.querySelectorAll('thead th[data-col]:not(.jt-sticky)'));
  }

  // Header keys in current DOM order, frozen block excluded.
  currentOrder() {
    return this.movableHeaders().map((th) => th.dataset.col);
  }

  // Move one column (header + every row cell) to sit before/after another.
  moveColumn(key, targetKey, before) {
    if (key === targetKey) return;
    this.tableTarget.querySelectorAll('tr').forEach((tr) => {
      const cell = tr.querySelector(`[data-col="${key}"]`);
      const target = tr.querySelector(`[data-col="${targetKey}"]`);
      if (!cell || !target) return;
      tr.insertBefore(cell, before ? target : target.nextSibling);
    });
    this.syncMenuOrder();
  }

  // Reorder every row to a full movable-key order (reset, and rows appended
  // by infinite scroll before a just-saved order reached the server).
  applyOrder(keys) {
    if (!keys || !keys.length || !this.hasTableTarget) return;
    const movable = keys.filter((k) => this.movableHeaders().some((th) => th.dataset.col === k));
    this.tableTarget.querySelectorAll('tr').forEach((tr) => this.applyRowOrder(tr, movable));
    this.syncMenuOrder();
  }

  applyRowOrder(tr, movable) {
    const fill = tr.querySelector('.jt-fill');
    movable.forEach((k) => {
      const cell = tr.querySelector(`[data-col="${k}"]`);
      if (cell) tr.insertBefore(cell, fill);
    });
  }

  // The Columns menu lists columns in table order; keep it that way.
  syncMenuOrder() {
    if (!this.hasMenuTarget || !this.hasTableTarget) return;
    const foot = this.menuTarget.querySelector('.jt-colmenu-foot');
    this.tableTarget.querySelectorAll('thead th[data-col]').forEach((th) => {
      const box = this.menuTarget.querySelector(`input[value="${th.dataset.col}"]`);
      const item = box && box.closest('.jt-colmenu-i');
      if (item) this.menuTarget.insertBefore(item, foot);
    });
  }

  // Rows infinite scroll appends are rendered in the order saved server-side;
  // if the user has just reordered, bring them in line with the header.
  watchAppendedRows() {
    if (!this.hasTableTarget || typeof MutationObserver === 'undefined') return;
    const tbody = this.tableTarget.querySelector('tbody');
    if (!tbody) return;
    this._rowObserver = new MutationObserver((mutations) => {
      const movable = this.currentOrder();
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1 || !node.matches('tr.jt-row')) return;
          const rowKeys = Array.from(node.querySelectorAll('td[data-col]'))
            .map((td) => td.dataset.col)
            .filter((k) => movable.includes(k));
          if (rowKeys.join() !== movable.join()) this.applyRowOrder(node, movable);
        });
      });
    });
    this._rowObserver.observe(tbody, { childList: true });
  }

  // ── Rendering ──────────────────────────────────────────────────────────────

  scheduleRender() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      this.render();
    });
  }

  render() {
    if (!this.hasStyleTarget || !this.hasTableTarget) return;

    const rules = [];
    this.hidden.forEach((key) => {
      rules.push(`#jobs-table [data-col="${key}"]{display:none}`);
    });
    Object.keys(this.widths).forEach((key) => {
      const px = this.widths[key];
      rules.push(
        `#jobs-table th[data-col="${key}"]>.jt-h,#jobs-table td[data-col="${key}"]>*` +
        `{width:${px}px;min-width:${px}px;max-width:${px}px}`
      );
    });

    // Apply visibility + widths first, then measure the frozen header cells
    // for their sticky offsets (which depend on those widths).
    this.styleTarget.textContent = rules.join('\n');
    rules.push(...this.stickyRules());
    this.styleTarget.textContent = rules.join('\n');

    this.syncBadge();
  }

  stickyRules() {
    const out = [];
    let left = 0;
    let lastKey = null;
    this.tableTarget.querySelectorAll('thead th.jt-sticky').forEach((th) => {
      const key = th.dataset.col;
      if (!key || this.hidden.has(key)) return;
      out.push(`#jobs-table [data-col="${key}"].jt-sticky{left:${left}px}`);
      left += th.getBoundingClientRect().width;
      lastKey = key;
    });
    if (lastKey) {
      out.push(`#jobs-table.is-scrolled-x [data-col="${lastKey}"].jt-sticky{box-shadow:6px 0 8px -6px rgba(20,28,40,.28)}`);
    }
    return out;
  }

  syncBadge() {
    if (!this.hasBadgeTarget) return;
    const count = this.hidden.size;
    this.badgeTarget.textContent = `${count} hidden`;
    this.badgeTarget.classList.toggle('hidden', count === 0);
  }

  // ── Persistence ────────────────────────────────────────────────────────────

  persist(options = {}) {
    if (!this.url) return;
    const formData = new FormData();
    if (options.reset) {
      formData.append('reset', '1');
    } else {
      // A blank sentinel keeps `hidden` present when nothing is hidden, so the
      // server can tell "show everything" from a request that omitted it.
      formData.append('hidden[]', '');
      this.hidden.forEach((key) => formData.append('hidden[]', key));
      Object.keys(this.widths).forEach((key) => formData.append(`widths[${key}]`, this.widths[key]));
      this.order.forEach((key) => formData.append('order[]', key));
    }

    fetch(this.url, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' },
      body: formData,
      credentials: 'same-origin'
    }).catch(() => { /* the in-page state already applied; next load falls back to the saved prefs */ });
  }

  parseJson(raw, fallback) {
    if (!raw) return fallback;
    try {
      const value = JSON.parse(raw);
      return value === null ? fallback : value;
    } catch (_) {
      return fallback;
    }
  }
}
